import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, inject,
  Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, SimpleChanges, viewChild
} from '@angular/core';

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { YouTubePlayer } from '@angular/youtube-player';
import { Subscription } from 'rxjs';

import { MediaTypeEnum } from '../banner/banner.enum';
import { DeviceService } from '../../../_services/device.service';
import { ImageRatioEnum } from '../../../_enums/image-ratio.enum';
import { IMedia } from '../../../_interfaces/media.interface';
import { VideoDsService } from '../../../_services/video-ds.service';

@Component({
  selector: 'sd-media[video]',
  templateUrl: './media-video.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [YouTubePlayer]
})
export class SdMediaVideoComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
  @Input()
  videoTitle: string;

  @Input()
  media: IMedia;

  @Input()
  isYoutube: boolean;

  @Input()
  customId: string;

  @Input()
  mediaTitle: string;

  @Input()
  isFit: boolean;

  @Input()
  controlsVideo: string = undefined;

  @Input()
  loopVideo: boolean;

  @Input()
  muted: boolean;

  @Input()
  autoplayVideo: boolean;

  @Input()
  isVideoThumbnail: boolean;

  @Input()
  imgRatio: ImageRatioEnum;

  @Input()
  hasBorder: boolean;

  @Input()
  hasAltText: boolean;

  @Input()
  isAriaHidden: boolean;

  @Input()
  isModal: boolean;

  @Input()
  hasModal = false;

  @Input()
  play: boolean;

  @Output()
  playChange = new EventEmitter<boolean>();

  @Output()
  mediaEnded = new EventEmitter<void>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.mediaTypeClass,
      this.isFit ? 'is-fit' : '',
      this.media?.urlTwicPic && !this.twicError ? 'is-twicpics' : '',
      this.imgRatio ? 'is-' + this.imgRatio : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  readonly targetPreviewMedia = viewChild('elementPreview', { read: ElementRef });
  readonly targetMedia = viewChild<ElementRef<HTMLVideoElement>>('element');
  readonly youtubePlayer = viewChild<YouTubePlayer>('player');

  private readonly deviceService = inject(DeviceService);
  private readonly videoDsService = inject(VideoDsService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  isLoading = true;
  isPlaying = false;
  mediaTypeClass: string;
  mediaType: MediaTypeEnum;
  mediaUrl: string;
  imageRatioEnum = ImageRatioEnum;
  deviceSubscription: Subscription;
  twicError = false;
  changes: MutationObserver;
  isDevice$: boolean;
  youtubePosterUrl = '';
  isYoutubeReady = false;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isYoutube) {
        if (!this.isYoutubeApiLoaded()) {
          this.loadYoutubeApi();
        } else {
          this.youtubeReady();
        }
      }
      this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (isPlatformBrowser(this.platformId)) {
      this.getMediaType();

      if (this.targetMedia() || this.youtubePlayer) {
        if (changes['play']) {
          this.togglePlay();
        }
      }
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.play = this.autoplayVideo;
      this.playChange.emit(this.autoplayVideo);
      this.isPlaying = this.autoplayVideo;
      this.getMediaType();

      if (this.isYoutube && !this.media?.previewMedia) {
        const posterUrl = `https://i.ytimg.com/vi/${this.mediaUrl}/${this.isDevice$ ? 'sddefault' : 'maxresdefault'}.jpg`;
        this.checkYoutubePosterImgExist(posterUrl, (exists) => {
          if (exists) {
            this.youtubePosterUrl = posterUrl;
          }
          this.dispatchVideoSnippets();
        });
      } else if (this.isYoutube && this.media?.previewMedia) {
        this.dispatchVideoSnippets();
      }

      if (this.isYoutube && this.autoplayVideo) {
        this.playMedia();
      }

      this.changes = new MutationObserver(() => {
        this.fallbackTwicpics();
      });

      if (this.targetPreviewMedia()) {
        this.changes.observe(this.targetPreviewMedia().nativeElement, {
          attributes: true,
          childList: true,
          characterData: true
        });
      }
    }
  }

  ngOnDestroy() {
    this.deviceSubscription?.unsubscribe();
    this.changes?.disconnect();
  }

  getMediaType() {
    if (this.isYoutube) {
      this.mediaUrl = this.media.videoId;
      this.mediaType = MediaTypeEnum.youtube;
      this.mediaTypeClass = 'is-media-' + MediaTypeEnum.video + ' is-media-' + this.mediaType;
    } else {
      this.mediaUrl = this.media.url;
      this.mediaType = MediaTypeEnum.video;
      this.mediaTypeClass = 'is-media-' + this.mediaType;
    }
  }

  fallbackTwicpics() {
    if (this.targetPreviewMedia()?.nativeElement.classList.contains('twic-error')) {
      if (
        this.media?.previewMedia?.url?.startsWith('https://sebplatform.api.groupe-seb.com') &&
        !this.media?.previewMedia?.url?.includes('w=') &&
        !this.media?.previewMedia?.url?.includes('fit=')
      ) {
        this.media.previewMedia.url =
          this.media?.previewMedia?.url?.replace('original/', '') +
          '?w=1920&fit=scale';
      }
      this.targetPreviewMedia()?.nativeElement.classList.remove('twic-error');
      this.twicError = true;
      this.cdRef.markForCheck();
    }
  }

  onVideoEnded() {
    if (this.isYoutube && this.loopVideo) {
      this.youtubePlayer()?.seekTo(0, false);
      setTimeout(() => {
        this.playMedia();
      }, 100);
    } else {
      this.play = false;
      this.playChange.emit(this.play);
      this.mediaEnded.emit();
      this.isPlaying = false;
    }
  }

  videoReady(status) {
    if (this.isYoutube) {
      if (this.muted) {
        this.youtubePlayer()?.mute();
      } else {
        this.youtubePlayer()?.unMute();
      }

      switch (status.data) {
        // Video ended
        case 0:
          this.play = false;
          this.isPlaying = false;
          this.playChange.emit(this.play);
          if (this.isYoutube) {
            this.onVideoEnded();
          }
          break;
        // Video is fully cached
        case 3:
          this.isLoading = false;
          break;
        // Video play
        case 1:
          this.isPlaying = true;
          this.play = true;
          this.playChange.emit(this.play);
          break;
        // Video paused
        case 2:
          this.play = false;
          this.playChange.emit(this.play);
          break;
      }
    } else {
      this.isLoading = false;
    }
  }

  togglePlay() {
    if (this.play) {
      this.playMedia();
    } else {
      this.pauseMedia();
    }
    this.isPlaying = true;
  }

  playMedia() {
    this.play = true;
    this.playChange.emit(this.play);
    if (!this.isYoutube) {
      this.targetMedia()?.nativeElement.play();
    } else {
      this.youtubePlayer()?.playVideo();
    }
    this.cdRef.markForCheck();
  }

  pauseMedia() {
    this.play = false;
    this.playChange.emit(this.play);
    if (!this.isYoutube) {
      this.targetMedia()?.nativeElement.pause();
    } else {
      this.youtubePlayer()?.pauseVideo();
    }
    this.cdRef.markForCheck();
  }

  checkYoutubePosterImgExist(url: string, callback) {
    const img = new Image();
    img.onload = () => {
      callback(true);
    };
    img.onerror = () => {
      callback(false);
    };
    img.src = url;
  }

  private loadYoutubeApi() {
    const script: HTMLScriptElement = this.document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.defer = true;
    script.onload = () => this.youtubeReady();
    this.document.body.appendChild(script);
  }

  private isYoutubeApiLoaded(): boolean {
    return !!this.document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]',
    );
  }

  dispatchVideoSnippets() {
    if (this.isYoutube && !this.isModal) {
      this.videoDsService.dispatchVideoInfo({
        videoTitle: this.videoTitle,
        videoId: this.mediaUrl,
        thumbnailUrl: this.media?.previewMedia?.url
          || this.media?.previewMedia?.urlTwicPic
          || this.youtubePosterUrl,
        creationTime: this.media?.creationTime
      });
    }
  }

  private youtubeReady(): void {
    setTimeout(() => {
      this.isYoutubeReady = true;
      this.cdRef.detectChanges();
    }, 100);
  }
}
